import React, { useState, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { Layout, Menu, Button, Tooltip, Avatar } from 'antd'
import {
  HomeOutlined, SettingOutlined, TeamOutlined, SafetyOutlined,
  FileSearchOutlined, ToolOutlined, ExperimentOutlined,
  BookOutlined, EditOutlined, TrophyOutlined,
  MenuFoldOutlined, MenuUnfoldOutlined,
  LeftOutlined, RightOutlined, UserOutlined,
  LogoutOutlined, DashboardOutlined, ApiOutlined,
  RocketOutlined, PictureOutlined, EyeOutlined,
} from '@ant-design/icons'
import { getUser, logout, hasRole, clearAuth, adminApi, getToken } from '../services/auth'

const { Sider, Header, Content, Footer } = Layout

// Menu configuration
const menuConfig = [
  {
    key: 'welcome',
    icon: <HomeOutlined />,
    label: '首页',
    roles: ['admin', 'process_admin', 'training_teacher', 'student'],
  },
  {
    key: 'system',
    icon: <SettingOutlined />,
    label: '系统管理',
    roles: ['admin'],
    children: [
      { key: 'admin/users', icon: <TeamOutlined />, label: '用户管理' },
      { key: 'admin/roles', icon: <SafetyOutlined />, label: '角色管理' },
      { key: 'admin/permissions', icon: <FileSearchOutlined />, label: '权限管理' },
      { key: 'admin/logs', icon: <ApiOutlined />, label: '日志审计' },
      { key: 'admin/config', icon: <SettingOutlined />, label: '系统配置' },
    ],
  },
  {
    key: 'process',
    icon: <ToolOutlined />,
    label: '工艺管理',
    roles: ['admin', 'process_admin', 'training_teacher'],
    children: [
      { key: 'process/icon-library', icon: <PictureOutlined />, label: '图标库管理', roles: ['admin'] },
      { key: 'process/types', icon: <ExperimentOutlined />, label: '流程管理', roles: ['admin', 'process_admin', 'training_teacher'] },
      { key: 'process/engine', icon: <SettingOutlined />, label: '引擎管理', roles: ['admin', 'process_admin'] },
    ],
  },
  {
    key: 'dict',
    icon: <BookOutlined />,
    label: '字典管理',
    roles: ['admin', 'process_admin'],
    children: [
      { key: 'dict/dict_isa_material', label: 'ISA介质类型' },
      { key: 'dict/dict_isa_material_class', label: 'ISA介质等级' },
      { key: 'dict/dict_isa_insulation', label: 'ISA保温类型' },
      { key: 'dict/dict_isa_equipment', label: 'ISA设备类型' },
      { key: 'dict/dict_isa_instrument', label: 'ISA仪表类型' },
      { key: 'dict/dict_pro_pipeline_specs', label: '管线规格' },
      { key: 'dict/dict_pro_vessel_specs', label: '容器规格' },
      { key: 'dict/dict_pro_exchanger_specs', label: '换热器规格' },
      { key: 'dict/dict_pro_pump_specs', label: '泵规格' },
      { key: 'dict/dict_pro_compressor_specs', label: '压缩机规格' },
      { key: 'dict/dict_pro_valve_specs', label: '阀门规格' },
      { key: 'dict/dict_pro_instrument_specs', label: '仪表规格' },
      { key: 'dict/dict_pro_pig_launcher_specs', label: '清管器规格' },
      { key: 'dict/dict_pro_device_specs', label: '设备规格' },
    ],
  },
  {
    key: 'training',
    icon: <TrophyOutlined />,
    label: '培训管理',
    roles: ['admin', 'training_teacher'],
    children: [
      { key: 'training/courses', icon: <BookOutlined />, label: '课程管理' },
      { key: 'training/exams', icon: <EditOutlined />, label: '考试管理' },
      { key: 'training/scoring', icon: <TrophyOutlined />, label: '考试评分' },
    ],
  },
  {
    key: 'learning',
    icon: <BookOutlined />,
    label: '学习管理',
    roles: ['admin', 'process_admin', 'training_teacher', 'student'],
    children: [
      { key: 'learning/study', icon: <BookOutlined />, label: '工艺学习' },
      { key: 'learning/exam', icon: <EditOutlined />, label: '参加考试' },
      { key: 'learning/observation-hall', icon: <EyeOutlined />, label: '观摩大厅' },
      { key: 'learning/scores', icon: <TrophyOutlined />, label: '成绩查询' },
    ],
  },
]

// Filter menu by user role
function filterMenu(items, userRole) {
  return items
    .filter(item => !item.roles || item.roles.includes(userRole))
    .map(item => ({
      ...item,
      children: item.children ? filterMenu(item.children, userRole) : undefined,
    }))
}

export default function AppLayout({ children }) {
  const navigate = useNavigate()
  const location = useLocation()
  const user = getUser()
  const [collapsed, setCollapsed] = useState(false)
  const [sidebarPos, setSidebarPos] = useState('LEFT')
  const [loading, setLoading] = useState(false)

  // Load sidebar position from config
  useEffect(() => {
    const token = getToken()
    fetch('/api/v1/admin/config/sidebar', {
      headers: { 'Authorization': `Bearer ${token}` }
    }).then(res => res.json()).then(res => {
      if (res?.data?.sidebarPosition) setSidebarPos(res.data.sidebarPosition)
    }).catch(() => {})
  }, [])

  // 监听侧边栏配置变化（系统配置页保存后立即生效）
  useEffect(() => {
    const handler = (e) => {
      if (e.detail?.sidebarPosition) setSidebarPos(e.detail.sidebarPosition)
    }
    window.addEventListener('sidebarConfigChanged', handler)
    return () => window.removeEventListener('sidebarConfigChanged', handler)
  }, [])

  // Determine selected key from path
  const selectedKey = '/' + location.pathname.split('/').filter(Boolean).join('/')

  const filteredMenu = user ? filterMenu(menuConfig, user.role) : []

  const handleMenuClick = ({ key }) => {
    navigate('/' + key)
  }

  const handleLogout = async () => {
    await logout()
    navigate('/login')
  }

  const handleProfile = () => {
    navigate('/profile')
  }

  const handleHome = () => {
    navigate('/welcome')
  }

  const toggleSidebar = () => setCollapsed(!collapsed)

  const siderWidth = collapsed ? 64 : 220

  const sider = (
    <Sider
      width={220}
      collapsible
      collapsed={collapsed}
      onCollapse={setCollapsed}
      trigger={null}
      style={{
        overflow: 'auto', height: '100vh', position: 'fixed',
        [sidebarPos === 'LEFT' ? 'left' : 'right']: 0, top: 0, zIndex: 10,
      }}
    >
      <div style={{
        height: 64, display: 'flex', alignItems: 'center', justifyContent: 'center',
        color: '#fff', fontSize: collapsed ? 14 : 16, fontWeight: 'bold',
        borderBottom: '1px solid rgba(255,255,255,0.1)',
        cursor: 'pointer', padding: '0 16px',
      }} onClick={toggleSidebar}>
        {collapsed ? '⚙️' : '⚙️ 工艺仿真系统'}
      </div>
      <Menu
        theme="dark"
        mode="inline"
        selectedKeys={[selectedKey]}
        defaultOpenKeys={[]}
        items={filteredMenu}
        onClick={handleMenuClick}
      />
    </Sider>
  )

  return (
    <Layout style={{ height: '100vh', overflow: 'hidden' }}>
      {sidebarPos === 'LEFT' ? sider : null}
      <Layout style={{ 
        marginLeft: sidebarPos === 'LEFT' ? siderWidth : 0, 
        marginRight: sidebarPos === 'RIGHT' ? siderWidth : 0,
        height: '100vh',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden'
      }}>
        {/* 上部固定区域 */}
        <Header style={{
          background: '#fff', padding: '0 24px', display: 'flex',
          alignItems: 'center', justifyContent: 'space-between',
          boxShadow: '0 1px 4px rgba(0,0,0,0.08)', position: 'sticky', top: 0, zIndex: 9,
          height: 48, lineHeight: '48px',
        }}>
          {/* 左侧：Logo、名称、版本 */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <RocketOutlined style={{ fontSize: 24, color: '#1890ff' }} />
            <span style={{ fontSize: 16, fontWeight: 'bold', color: '#333' }}>
              LH-SIMULATE-PROCESS
            </span>
            <span style={{ color: '#999', fontSize: 12, fontFamily: 'monospace' }}>
              {__FULL_VERSION__}
            </span>
          </div>
          
          {/* 右侧：home、用户头像、退出 */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Tooltip title="首页">
              <Button 
                type="text" 
                icon={<HomeOutlined style={{ fontSize: 18 }} />} 
                onClick={handleHome}
              />
            </Tooltip>
            <Tooltip title="个人资料">
              <Avatar 
                icon={<UserOutlined />} 
                style={{ cursor: 'pointer', backgroundColor: '#1890ff' }}
                onClick={handleProfile}
              />
            </Tooltip>
            <Tooltip title="退出登录">
              <Button 
                type="text" 
                icon={<LogoutOutlined style={{ fontSize: 18, color: '#ff4d4f' }} />} 
                onClick={handleLogout}
              />
            </Tooltip>
          </div>
        </Header>
        
        {/* 中部可滚动内容区 */}
        <Content style={{
          margin: '16px 16px 0 16px',
          flex: 1,
          overflow: 'hidden',
          minHeight: 0
        }}>
          {children}
        </Content>
        
        {/* 下部固定版权声明 */}
        <div style={{
          textAlign: 'center',
          padding: '6px 0',
          borderTop: '1px solid #e8e8e8',
          fontSize: 12,
          color: '#999',
          background: '#fff',
          flexShrink: 0,
          lineHeight: '20px'
        }}>
          北京其点技术服务有限公司 版权所有@2026
        </div>
      </Layout>
      {sidebarPos === 'RIGHT' ? sider : null}
    </Layout>
  )
}
